import { useEffect, useMemo, useRef } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Plus, Send, Trash2 } from "lucide-react";
import {
  CollapsibleBlock,
  DocumentHeader,
  DocumentPage,
  DocumentStickyBar,
  useDocumentSaveShortcut,
} from "@/components/shared/document-shell";
import { EntityPicker } from "@/components/shared/entity-picker";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, FormSection } from "@/components/shared/form";
import {
  LineGrid,
  LineGridBody,
  LineGridHead,
  LineGridRow,
  LineGridTd,
  LineGridTh,
  blockNumberWheel,
  cellControlClass,
} from "@/components/shared/line-grid";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatMoney, PAYMENT_TERMS } from "@/features/clients/constants";
import { useClients } from "@/features/clients/api";
import { useMaterialGrades, useMaterials } from "@/features/materials/api";
import { computeLineAmounts, computePoTotals } from "@/features/purchase/api";
import {
  useCreateQuotation,
  useQuotation,
  useUpdateQuotation,
  quotationToForm,
} from "@/features/quotations/api";
import { canEditLines } from "@/features/quotations/constants";
import {
  emptyQtLine,
  emptyQuotationForm,
  quotationFormSchema,
  type QuotationFormValues,
} from "@/features/quotations/schema";
import { GST_SLABS, listGstSlabs, resolveApplicableTaxes } from "@/features/settings/constants";
import { useCompany, useTaxRates } from "@/features/settings/api";

export function QuotationNewPage() {
  return (
    <ErrorBoundary title="Quotation form crashed">
      <QuotationFormPage mode="create" />
    </ErrorBoundary>
  );
}

export function QuotationEditPage({ qtId }: { qtId: string }) {
  return (
    <ErrorBoundary title="Quotation form crashed">
      <QuotationFormPage mode="edit" qtId={qtId} />
    </ErrorBoundary>
  );
}

function QuotationFormPage({ mode, qtId }: { mode: "create" | "edit"; qtId?: string }) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { client?: string };
  const isEdit = mode === "edit";
  const { data: qt, isLoading } = useQuotation(isEdit ? qtId : undefined);
  const { data: clients = [] } = useClients();
  const { data: materials = [] } = useMaterials();
  const { data: grades = [] } = useMaterialGrades();
  const { data: company } = useCompany();
  const { data: taxRates = [] } = useTaxRates();
  const createQt = useCreateQuotation();
  const updateQt = useUpdateQuotation();
  const hydratedRef = useRef(false);

  const form = useForm<QuotationFormValues>({
    resolver: zodResolver(quotationFormSchema),
    defaultValues: emptyQuotationForm,
    mode: "onBlur",
  });

  const { fields, append, remove } = useFieldArray({ control: form.control, name: "lines" });
  const values = useWatch({ control: form.control });
  const linesEditable = !isEdit || (qt ? canEditLines(qt.status) : true);
  const clientId = values.client_id;
  const client = clients.find((c) => c.id === clientId);
  const {
    formState: { errors },
  } = form;

  useEffect(() => {
    if (!isEdit || !qt || hydratedRef.current) return;
    form.reset(quotationToForm(qt));
    hydratedRef.current = true;
  }, [isEdit, qt, form]);

  useEffect(() => {
    if (isEdit || !search.client || form.getValues("client_id")) return;
    form.setValue("client_id", search.client, { shouldDirty: true });
    const c = clients.find((x) => x.id === search.client);
    if (c?.payment_terms) form.setValue("payment_terms", c.payment_terms, { shouldDirty: true });
    if (c) {
      const bill = [c.name, c.addr1, [c.city, c.pincode].filter(Boolean).join(" — ")]
        .filter(Boolean)
        .join("\n");
      form.setValue("bill_to_text", bill, { shouldDirty: true });
    }
  }, [isEdit, search.client, clients, form]);

  const clientOptions = useMemo(
    () =>
      clients
        .filter((c) => c.is_active)
        .map((c) => ({ id: c.id, label: `${c.code} · ${c.name}`, keywords: c.name })),
    [clients],
  );

  const materialOptions = useMemo(
    () =>
      materials
        .filter((m) => m.is_active && !m.parent_material_id)
        .map((m) => ({ id: m.id, label: m.name, keywords: m.batch_prefix ?? "" })),
    [materials],
  );

  const slabs = useMemo(() => {
    const fromRates = listGstSlabs(taxRates);
    return fromRates.length ? fromRates : [...GST_SLABS];
  }, [taxRates]);

  const preview = useMemo(() => {
    const lines = values.lines ?? [];
    const clientPos = client?.place_of_supply || client?.state;
    const computed = lines.map((line) =>
      computeLineAmounts(
        {
          qty: Number(line.qty) || 0,
          weight: Number(line.weight) || 0,
          unit_rate: Number(line.unit_rate) || 0,
          gst_slab: Number(line.gst_slab) || 0,
        },
        taxRates,
        company?.state,
        clientPos,
        values.issue_date ?? emptyQuotationForm.issue_date,
      ),
    );
    const totals = computePoTotals(
      computed,
      Number(values.discount) || 0,
      Number(values.adjustment) || 0,
    );
    const { mode: gstMode } = resolveApplicableTaxes({
      rates: taxRates,
      gstSlab: 18,
      companyState: company?.state,
      clientPlaceOfSupply: clientPos,
      asOf: values.issue_date,
    });
    return { lines: computed, totals, mode: gstMode };
  }, [values, taxRates, company?.state, client]);

  const saving = createQt.isPending || updateQt.isPending;

  async function save(send = false) {
    const ok = await form.trigger();
    if (!ok) return;
    const data = form.getValues();
    if (isEdit && qtId) {
      const updated = await updateQt.mutateAsync({
        id: qtId,
        values: data,
        replaceLines: true,
        send,
      });
      navigate({ to: "/quotations/$qtId", params: { qtId: updated.id } });
    } else {
      const created = await createQt.mutateAsync({ values: data, send });
      navigate({ to: "/quotations/$qtId", params: { qtId: created.id } });
    }
  }

  useDocumentSaveShortcut(() => save(false), saving);

  if (isEdit && isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (isEdit && qt && !canEditLines(qt.status)) {
    return (
      <div className="py-16 text-center text-sm">
        <p className="text-muted-foreground">This quotation cannot be edited.</p>
        <Button asChild variant="outline" size="sm" className="mt-3">
          <Link to="/quotations/$qtId" params={{ qtId: qt.id }}>
            View quotation
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <DocumentPage>
      <DocumentHeader
        title={isEdit ? `Edit ${qt?.qt_number ?? "quotation"}` : "New quotation"}
        back={
          <Button asChild variant="ghost" size="icon-sm">
            {isEdit && qtId ? (
              <Link to="/quotations/$qtId" params={{ qtId }}>
                <ArrowLeft />
              </Link>
            ) : (
              <Link to="/quotations">
                <ArrowLeft />
              </Link>
            )}
          </Button>
        }
        actions={
          <>
            <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => save(false)}>
              {saving ? <Loader2 className="animate-spin" /> : null}
              Save draft
            </Button>
            {linesEditable ? (
              <Button type="button" size="sm" disabled={saving} onClick={() => save(true)}>
                <Send />
                Save & send
              </Button>
            ) : null}
          </>
        }
      />

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          save(false);
        }}
      >
        <FormSection title="Header" variant="plain">
          <FieldGrid>
            <Field label="Client" required span={4} error={errors.client_id?.message}>
              <EntityPicker
                value={values.client_id ?? ""}
                disabled={!linesEditable && isEdit}
                placeholder="Type client code or name…"
                recentKey="clients"
                options={clientOptions}
                onChange={(id) => {
                  form.setValue("client_id", id, { shouldDirty: true, shouldValidate: true });
                  const c = clients.find((x) => x.id === id);
                  if (c?.payment_terms) {
                    form.setValue("payment_terms", c.payment_terms, { shouldDirty: true });
                  }
                  if (c) {
                    const bill = [c.name, c.addr1, [c.city, c.pincode].filter(Boolean).join(" — ")]
                      .filter(Boolean)
                      .join("\n");
                    form.setValue("bill_to_text", bill, { shouldDirty: true });
                  }
                }}
              />
            </Field>
            <Field label="Issue" required span={2} error={errors.issue_date?.message}>
              <Input type="date" {...form.register("issue_date")} />
            </Field>
            <Field label="Valid until" span={2}>
              <Input type="date" {...form.register("valid_until")} />
            </Field>
            <Field label="Payment" span={4}>
              <NativeSelect {...form.register("payment_terms")}>
                <option value="">—</option>
                {PAYMENT_TERMS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </FieldGrid>

          <CollapsibleBlock title="Bill to / Ship to" className="mt-1">
            <FieldGrid>
              <Field label="Bill to" span={6}>
                <Textarea rows={2} className="min-h-0 text-xs" {...form.register("bill_to_text")} />
              </Field>
              <Field label="Ship to" span={6}>
                <Textarea rows={2} className="min-h-0 text-xs" {...form.register("ship_to_text")} />
              </Field>
            </FieldGrid>
          </CollapsibleBlock>
        </FormSection>

        <FormSection
          title="Lines"
          variant="plain"
          aside={
            linesEditable ? (
              <Button type="button" size="xs" variant="outline" onClick={() => append(emptyQtLine())}>
                <Plus />
                Add
              </Button>
            ) : null
          }
        >
          {errors.lines?.message ? (
            <p className="mb-1 text-2xs text-destructive">{errors.lines.message}</p>
          ) : null}

          <LineGrid minWidth="56rem">
            <LineGridHead>
              <LineGridTh className="w-7 text-center">#</LineGridTh>
              <LineGridTh className="min-w-[8rem]">Material</LineGridTh>
              <LineGridTh className="min-w-[4.5rem]">Grade</LineGridTh>
              <LineGridTh className="w-14" align="right">
                Qty
              </LineGridTh>
              <LineGridTh className="w-14" align="right">
                Wt
              </LineGridTh>
              <LineGridTh className="w-[4.5rem]" align="right">
                Rate
              </LineGridTh>
              <LineGridTh className="w-[4.75rem]">GST</LineGridTh>
              <LineGridTh className="min-w-[7rem]">Spec</LineGridTh>
              <LineGridTh className="w-20" align="right">
                Amt
              </LineGridTh>
              <LineGridTh className="w-7" />
            </LineGridHead>
            <LineGridBody>
              {fields.map((field, index) => {
                const lineAmount = preview.lines[index]?.line_subtotal ?? 0;
                const taxHint = preview.lines[index]?.taxes ?? [];
                const materialId = values.lines?.[index]?.material_id ?? "";
                const gradeOptions = grades.filter((g) => g.is_active);
                const gstSlab = Number(values.lines?.[index]?.gst_slab) || 0;
                const { taxes } = resolveApplicableTaxes({
                  rates: taxRates,
                  gstSlab,
                  companyState: company?.state,
                  clientPlaceOfSupply: client?.place_of_supply || client?.state,
                  asOf: values.issue_date,
                });
                const gstLabel =
                  preview.mode === "intra_state" && taxes.length >= 2
                    ? `${gstSlab}% (${taxes.map((t) => t.rate).join("+")})`
                    : `${gstSlab}% (IGST)`;

                return (
                  <LineGridRow key={field.id}>
                    <LineGridTd className="text-center text-muted-foreground">{index + 1}</LineGridTd>
                    <LineGridTd>
                      <EntityPicker
                        dense
                        value={materialId}
                        disabled={!linesEditable}
                        placeholder="Material…"
                        options={materialOptions}
                        onChange={(id) => {
                          form.setValue(`lines.${index}.material_id`, id, { shouldDirty: true });
                          form.setValue(`lines.${index}.material_grade_id`, "", { shouldDirty: true });
                        }}
                      />
                    </LineGridTd>
                    <LineGridTd>
                      <NativeSelect
                        className={cellControlClass()}
                        disabled={!linesEditable || !materialId}
                        {...form.register(`lines.${index}.material_grade_id`)}
                      >
                        <option value="">—</option>
                        {gradeOptions.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                      </NativeSelect>
                    </LineGridTd>
                    <LineGridTd align="right">
                      <Input
                        type="number"
                        step="1"
                        min={0}
                        disabled={!linesEditable}
                        className={cellControlClass()}
                        onWheel={blockNumberWheel}
                        {...form.register(`lines.${index}.qty`)}
                      />
                    </LineGridTd>
                    <LineGridTd align="right">
                      <Input
                        type="number"
                        step="0.001"
                        min={0}
                        disabled={!linesEditable}
                        className={cellControlClass()}
                        onWheel={blockNumberWheel}
                        {...form.register(`lines.${index}.weight`)}
                      />
                    </LineGridTd>
                    <LineGridTd align="right">
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        disabled={!linesEditable}
                        className={cellControlClass()}
                        onWheel={blockNumberWheel}
                        {...form.register(`lines.${index}.unit_rate`)}
                      />
                    </LineGridTd>
                    <LineGridTd>
                      <NativeSelect
                        className={cellControlClass()}
                        disabled={!linesEditable}
                        {...form.register(`lines.${index}.gst_slab`)}
                      >
                        {slabs.map((s) => (
                          <option key={s} value={s}>
                            {s === gstSlab ? gstLabel : `${s}%`}
                          </option>
                        ))}
                      </NativeSelect>
                    </LineGridTd>
                    <LineGridTd>
                      <Input
                        disabled={!linesEditable}
                        className={cellControlClass()}
                        placeholder="Spec / notes"
                        {...form.register(`lines.${index}.description`)}
                      />
                    </LineGridTd>
                    <LineGridTd align="right" className="tabular-nums">
                      {formatMoney(lineAmount)}
                      {taxHint.length ? (
                        <span className="block text-2xs text-muted-foreground">
                          +{formatMoney(taxHint.reduce((s, t) => s + t.amount, 0))}
                        </span>
                      ) : null}
                    </LineGridTd>
                    <LineGridTd>
                      {fields.length > 1 && linesEditable ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => remove(index)}
                        >
                          <Trash2 />
                        </Button>
                      ) : null}
                    </LineGridTd>
                  </LineGridRow>
                );
              })}
            </LineGridBody>
          </LineGrid>
        </FormSection>

        <CollapsibleBlock title="Notes & terms">
          <FieldGrid>
            <Field label="Notes" span={6}>
              <Textarea rows={2} className="text-xs" {...form.register("notes")} />
            </Field>
            <Field label="Terms" span={6}>
              <Textarea rows={2} className="text-xs" {...form.register("terms")} />
            </Field>
          </FieldGrid>
        </CollapsibleBlock>

        <CollapsibleBlock title="Discount & adjustment">
          <FieldGrid>
            <Field label="Discount" span={3}>
              <Input
                type="number"
                step="0.01"
                className="fs-no-spin"
                onWheel={blockNumberWheel}
                {...form.register("discount")}
              />
            </Field>
            <Field label="Adjustment" span={3} hint="Can be negative">
              <Input
                type="number"
                step="0.01"
                className="fs-no-spin"
                onWheel={blockNumberWheel}
                {...form.register("adjustment")}
              />
            </Field>
          </FieldGrid>
        </CollapsibleBlock>
      </form>

      <DocumentStickyBar>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span>
            Sub <strong className="tabular-nums">{formatMoney(preview.totals.subtotal)}</strong>
          </span>
          <span>
            Tax <strong className="tabular-nums">{formatMoney(preview.totals.tax_total)}</strong>
          </span>
          <span>
            Grand{" "}
            <strong className="tabular-nums text-primary">
              {formatMoney(preview.totals.grand_total)}
            </strong>
          </span>
          <span className="text-2xs text-muted-foreground">
            {preview.mode === "intra_state" ? "CGST+SGST" : "IGST"} · {fields.length} lines
          </span>
        </div>
      </DocumentStickyBar>
    </DocumentPage>
  );
}
